import React, { useState, useEffect, useContext } from 'react';
import { getRedirectResult } from "firebase/auth";
import { auth } from './firebase.js';
import { useDispatch } from 'react-redux';
import { login } from './userSlice';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import Spinner from './Spinner.js';
import bgImage from './images/truck_blue_snow.jpg';
import ContactForm from './ContactUsForm.js';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import queryString from "query-string";
import ReactPixel from "react-facebook-pixel";
import { useLocation } from "react-router-dom";

function IntegrityLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUser, loginWithEmail } = useContext(AuthContext);
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);

  const { carrierPortal } = theme;
  const location = useLocation();

  useEffect(() => {
    const params = queryString.parse(window.location.search);
    if (Object.keys(params).length > 0) {
      localStorage.setItem("marketingParams", JSON.stringify(params));
    }
  }, []);

  useEffect(() => {
    const pageName = location.pathname.replace("/", "") || "Home";
    ReactPixel.pageView({
      page: pageName,
      path: location.pathname,
      url: window.location.href
    });
  }, [location]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const loginTimeout = setTimeout(() => {
      setIsLoading(false);
      setError('Login process is taking too long. Please try again.');
    }, 15000);

    try {
      await loginWithEmail(email, password);
      clearTimeout(loginTimeout);
    } catch (err) {
      console.error("Error Logging In:", err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      // Navigate based on user type
      if (currentUser.isAffiliate === 'True' || currentUser.isAffiliate === true || currentUser.Roles === 'Affiliate') {
        navigate('/affiliate-dashboard');
      } else {
        navigate('/dashboard');
      }
    } else {
      getRedirectResult(auth)
        .then((result) => {
          if (result && result.user) {
            const { displayName, email, uid, photoURL } = result.user;
            dispatch(login({ displayName, email, uid, photoURL }));
            // Navigation will be handled by the updated currentUser in the next render
          }
        })
        .catch((error) => {
          console.error("Error in getRedirectResult:", error);
          setError(error.message);
        });
    }
  }, [currentUser, dispatch, navigate]);

  return (
    <>
      {isLoading && <Spinner />}

      <div className="min-h-screen flex flex-col bg-white text-[#1e365c]">
        {/* Header */}
        <header className="top-0 z-50 bg-white shadow-md">
          <div className="container mx-auto px-6 py-2 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="/logos/integrity_logo_big.png"
                alt="Integrity Factoring Logo"
                className="h-20 w-auto object-contain"
              />
            </div>
            <nav className="hidden md:flex space-x-6">
              <Link to="/integritydemo" className="text-[#1e365c] hover:text-[#00b1da] transition">
                Explore Our Discounts
              </Link>
              <a href="#contact" className="text-[#1e365c] hover:text-[#00b1da] transition">
                Contact
              </a>
              <Link to="/registration" className="text-[#1e365c] hover:text-[#00b1da] transition">
                Sign Up
              </Link>
            </nav>
            <div className="md:hidden">
              <button
                onClick={() => setIsNavOpen(!isNavOpen)}
                className="text-[#1e365c] focus:outline-none"
              >
                <FontAwesomeIcon icon={faBars} className="text-[#00b1da] text-4xl" />
              </button>
            </div>
          </div>
          <nav
            className={`md:hidden transition-all duration-300 bg-white ${isNavOpen ? 'block' : 'hidden'}`}
          >
            <div className="px-6 py-2">
              <Link
                to="/integritydemo"
                className="block text-[#1e365c] hover:bg-[#00b1da] hover:text-white rounded py-2"
                onClick={() => setIsNavOpen(false)}
              >
                Explore Our Discounts
              </Link>
              <a
                href="#contact"
                className="block text-[#1e365c] hover:bg-[#00b1da] hover:text-white rounded py-2"
                onClick={() => setIsNavOpen(false)}
              >
                Contact
              </a>
              <Link
                to="/registration"
                className="block text-[#1e365c] hover:bg-[#00b1da] hover:text-white rounded py-2"
                onClick={() => setIsNavOpen(false)}
              >
                Sign Up
              </Link>
            </div>
          </nav>
        </header>

        {/* Hero Section */}
        <div
          className="bg-no-repeat bg-cover bg-center md:bg-fixed text-[#1e365c]"
          style={{ backgroundImage: `url(${bgImage})`, backgroundAttachment: 'fixed' }}
        >
          <div className="mx-auto px-6 md:px-24 py-10 flex flex-col md:flex-row items-center bg-[#1e365c]/60">
            <div className="w-full md:w-2/3">
              <h1 className="text-5xl font-bold mb-4 text-[#ffb636]">
                Fuel Up Fast. Save Big. Drive On.
              </h1>
              <p className="text-lg md:text-lg mb-6 text-white">
                Instant access to fuel savings and funds with Integrity Factoring. No hassle, no waiting—just results.
              </p>
            </div>
            <div className="w-full md:w-1/4 mt-8 md:mt-0 bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl mb-4 text-center text-[#1e365c]">Login</h2>
              {error && <p className="text-red-500 mb-4">{error}</p>}
              <form onSubmit={handleLogin}>
                <div className="mb-4">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-2 border border-[#1e365c] rounded bg-white text-[#1e365c] focus:ring-2 focus:ring-[#00b1da]"
                  />
                </div>
                <div className="mb-4">
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-2 border border-[#1e365c] rounded bg-white text-[#1e365c] focus:ring-2 focus:ring-[#00b1da]"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-[#00b1da] hover:bg-[#ffb636] text-white font-bold p-2 rounded mb-4 transition duration-300"
                >
                  Login
                </button>
              </form>
              <div className="text-center text-[#1e365c]">
                {!carrierPortal && (
                  <div>
                    <Link to="/registration" className="text-[#00b1da] hover:underline">Sign Up</Link>
                  </div>
                )}
                <div>
                  <Link to="/resetpassword" className="text-[#00b1da] hover:underline">Forgot Password?</Link>
                </div>
                {!carrierPortal && (
                  <div>
                    <Link to="/faq" className="text-[#00b1da] hover:underline">FAQ's</Link>
                  </div>
                )}
                {carrierPortal && (
                  <Link to="/signup" className="text-[#00b1da] hover:underline">Redeem an Invitation Code</Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-gradient-to-b from-white to-[#e6f0fa] text-[#1e365c] py-16">
          <div className="mx-auto px-6">
            <div className="space-y-8">
              <div className="flex flex-col bg-white rounded-lg shadow-md p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#ffb636]">Instant Access, No Hassle!</h3>
                  <p className="text-gray-700 mt-2">
                    Get your prepaid fuel card and start saving immediately. Zero upfront costs, no credit check, and free to sign up.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <img
                    src="/images/integrity_dashboard_display.png" 
                    alt="Fuel Transactions"
                    className="w-full h-48 object-contain rounded-lg"
                  />
                  <img
                    src="/images/integrity_card_display.png" 
                    alt="Integrity Fuel Card"
                    className="w-full h-48 object-contain rounded-lg"
                  />
                  <img
                    src="/images/integrity_location_finder_display.png" 
                    alt="Fuel Finder"
                    className="w-full h-48 object-contain rounded-lg"
                  />
                </div>
              </div>

              <div className="flex flex-col bg-white rounded-lg shadow-md p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#ffb636]">Big Savings & Nationwide Coverage</h3>
                  <p className="text-gray-700 mt-2">
                    Save an average of $0.40 per gallon at over 4,000+ fueling locations nationwide. Wherever you go, we’ve got you covered.
                  </p>
                  <div className="text-center mt-4">
                    <Link
                      to="/integritydemo"
                      className="inline-block px-6 py-3 bg-[#00b1da] hover:bg-[#ffb636] text-white font-semibold rounded-md shadow-lg transition duration-300"
                    >
                      Explore Our Discounts
                    </Link>
                  </div>
                  <p className="mt-5 text-gray-700">Some of our partners include:</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                  <div className="col-span-1 md:col-span-3 flex justify-center my-4">
                    <img
                      src="/logos/ta-tri-logo-primary.svg"
                      alt="Combined Fuel Locations"
                      className="w-3/4 h-32 object-contain"
                    />
                  </div>
                  <div className="col-span-1 md:col-span-3 flex justify-center my-4 flex-wrap">
                    <img src="/logos/speedwayLogo.svg" alt="Speedway" className="w-24 h-24 object-contain mx-2" />
                    <img src="/logos/racetracLogo.png" alt="RaceTrac" className="w-24 h-24 object-contain mx-2" />
                    <img src="/logos/roadRangerLogo.png" alt="Road Ranger" className="w-24 h-24 object-contain mx-2" />
                    <img src="/logos/quarlesLogo.svg" alt="Quarles" className="w-24 h-24 object-contain mx-2" />
                    <img src="/logos/caseysLogo.svg" alt="Casey's" className="w-24 h-24 object-contain mx-2" />
                  </div>
                  <div className="col-span-1 md:col-span-3 flex justify-center my-4 space-x-4 flex-wrap">
                    <img src="/logos/sappBrosLogo.png" alt="Sapp Bros" className="w-32 h-28 object-contain" />
                    <img src="/logos/maverikLogo.svg" alt="Maverik" className="w-32 h-28 object-contain" />
                    <img src="/logos/ambest-logo.png" alt="AM Best" className="w-32 h-28 object-contain" />
                  </div>
                </div>
              </div>

              <div className="flex flex-col bg-white rounded-lg shadow-md p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#ffb636]">Fuel Finder & Card Management</h3>
                  <p className="text-gray-700 mt-2">
                    Find the lowest-priced fuel with our Fuel Finder and manage multiple cards from an intuitive platform—all from your phone.
                  </p>
                  <p className="text-gray-700 mt-2">
                    Add and manage multiple fuel cards with authorized users you can activate or deactivate anytime. Stay in control of your fleet.
                  </p>
                </div>
              </div>

              <div className="flex flex-col bg-white rounded-lg shadow-md p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#ffb636]">Instant Access to Funds with Tank</h3>
                  <p className="text-gray-700 mt-2">
                    Partnered with Tank Payments, Integrity Factoring delivers rapid factoring payouts so you’re never waiting for funds. 
                    With your Tank account, instantly transfer factored funds to your Integrity fuel cards for immediate use at thousands of locations. 
                    Fuel up, pay expenses, and keep your fleet moving without delay.
                  </p>
                  <div className="flex justify-center mt-4">
                    <img
                      src="https://cdn.prod.website-files.com/62291652a86b53583094c14a/645299ffbb7d424d80367c1c_Tank%20Nav%20Logo.svg"
                      alt="Tank Payments Logo"
                      className="h-12 w-auto object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="contact" className="bg-[#1e365c] text-white py-16">
          <ContactForm />
        </div>

        <footer className="bg-[#1e365c] text-white py-6">
          <div className="container mx-auto px-6 text-center">
            © {new Date().getFullYear()} Integrity Factoring. All rights reserved.
          </div>
        </footer>
      </div>
    </>
  );
}

export default IntegrityLogin;